import { useState, useRef, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
  size?: 'default' | 'sm';
  tooltip?: string;
}

/** Recherche insensible à la casse et aux accents (« societe » trouve « Société »). */
const normalizeForSearch = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Select avec recherche sur une liste d'options déjà chargée (filtrage côté
 * client). Même rendu que FormSelect / AutocompleteSelect ; à préférer à
 * AutocompleteSelect quand les options ne viennent pas d'un endpoint paginé
 * avec paramètre de recherche.
 */
export const SearchableSelect = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  error,
  disabled = false,
  dataTestId,
  className = '',
  size = 'default',
  tooltip,
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const term = normalizeForSearch(search.trim());
    if (!term) return options;
    return options.filter((option) => normalizeForSearch(option.label).includes(term));
  }, [options, search]);

  const open = () => {
    if (!disabled) setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  };

  const highlight = (index: number) => {
    setHighlightedIndex(index);
    listRef.current?.children[index]?.scrollIntoView({ block: 'nearest' });
  };

  const handleSelect = (option: SearchableSelectOption) => {
    onChange(option.value);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        open();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        highlight(Math.min(highlightedIndex + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        highlight(Math.max(highlightedIndex - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  };

  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (sm ? 'tw-py-1' : 'tw-py-2.5');
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm ? 'tw-top-[9px] tw-text-sm tw-text-gray-400' : 'tw-top-[13px] tw-text-base tw-text-gray-400';
  const isLabelFloated = !!selectedOption || isOpen;

  return (
    <div className={className}>
      <div className="tw-relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          data-testid={dataTestId}
          disabled={disabled}
          className={`tw-w-full tw-pl-3 tw-pr-10 ${paddingClass} ${textClass} tw-border ${sm ? 'tw-rounded-lg' : 'tw-rounded-xl'} tw-transition-all tw-bg-white
            ${error ? 'tw-border-red-500' : 'tw-border-gray-200'}
            ${isOpen ? 'tw-placeholder-gray-400' : 'tw-placeholder-transparent'}
            focus:tw-outline-none focus:tw-ring-2
            ${error ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary'}
            disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500`}
          placeholder={selectedOption?.label || placeholder || ' '}
          value={isOpen ? search : selectedOption?.label ?? ''}
          onChange={(e) => {
            setSearch(e.target.value);
            setHighlightedIndex(0);
            open();
          }}
          onFocus={open}
          onClick={open}
          onBlur={close}
          onKeyDown={handleKeyDown}
        />

        {label && (
          <label
            className={`tw-absolute tw-left-3 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${isLabelFloated ? labelFloated : labelUnfloated}
              ${isLabelFloated ? (error ? 'tw-text-red-500' : 'tw-text-affinicar-primary') : ''}
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}

        <ChevronDown
          size={sm ? 16 : 20}
          className={`tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-pointer-events-none tw-text-gray-400 tw-transition-transform ${
            isOpen ? 'tw-rotate-180' : ''
          }`}
        />

        {isOpen && (
          <div
            className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-auto"
            // Garde le focus sur l'input (clic sur une option ou sur la barre de
            // défilement) : sinon le blur fermerait la liste avant le clic
            onMouseDown={(e) => e.preventDefault()}
          >
            <ul ref={listRef} role="listbox" className="tw-py-1 tw-pl-0 tw-mb-0">
              {filteredOptions.length === 0 && (
                <li className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-sm">
                  Aucun résultat trouvé
                </li>
              )}

              {filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className={`tw-px-4 tw-py-2 ${textClass} tw-cursor-pointer tw-transition-colors tw-border-b tw-border-gray-100 last:tw-border-0 ${
                      isSelected ? 'tw-bg-affinicar-lighter tw-text-blue-700' : ''
                    } ${
                      isHighlighted && !isSelected ? 'tw-bg-gray-100' : ''
                    } hover:tw-bg-gray-100`}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {error && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>
      )}
    </div>
  );
};
